import type { FilterState, Task } from '@/types';
import { isCompletedStatus, normalizeStatus } from '@/constants/taskStatuses';
import { buildTaskPath, matchesFilterPath } from './pathUtils';

interface Container {
  _id: string;
  type: 'space' | 'folder' | 'list';
  name: string;
  path: string;
  displayPath?: string;
}

/**
* Apply global task filters in one consistent place.
*
* Rules:
* - Filters are global and must behave the same across all views.
* - exclude-missing: if a filter is active and the task lacks that value, the task is excluded.
*
* @param tasks - Array of tasks to filter
* @param filters - Filter state containing active filters
* @param containersMap - Optional map of container IDs to container info, used for spaceId-based filtering when tasks aren't enriched
*/
export function applyTaskFilters(
  tasks: Task[],
  filters: FilterState,
  containersMap?: Map<string, Container>,
  resolveDisplayName?: (id: string) => string,
): Task[] {
  const statusSet = new Set(filters.status.map(s => normalizeStatus(s)));
  const prioritySet = new Set(filters.priority);
  const assigneeSet = new Set(filters.assignees);
  const tagSet = new Set(filters.tags);

  const hasStatusFilter = statusSet.size > 0;
  const hasPriorityFilter = prioritySet.size > 0;
  const hasAssigneeFilter = assigneeSet.size > 0;
  const hasTagFilter = tagSet.size > 0;
  const hasDateRangeFilter = Boolean(filters.dateRange.start || filters.dateRange.end);
  const hasCustomFieldsFilter = filters.customFields && Object.keys(filters.customFields).length > 0;

  // For hasSubtasks, we need to know which task IDs are referenced as parentId.
  let parentIds: Set<string> | null = null;
  if (filters.hasSubtasks !== null) {
  parentIds = new Set<string>();
  for (const t of tasks) {
      if (t.parentId) parentIds.add(t.parentId);
  }
  }

  const searchLower = filters.search.trim().toLowerCase();
  const hasSearch = searchLower.length > 0;

  const filteredResult = tasks.filter(task => {
  const taskStatus = normalizeStatus(task.status);

  // Hide completed tasks unless showCompleted is true
  if (!filters.showCompleted) {
      if (isCompletedStatus(taskStatus)) return false;
  }

  // Path filter - match if task path starts with filter path
  if (filters.path) {
      // Task path should be stored in task.path or constructed from spaceName/folderPath/listName
      const taskWithPath = task as Task & { path?: string };
      const taskPath = taskWithPath.path || buildTaskPath(task.spaceName, task.folderPath, task.listName);

      // If task has no path info yet (not enriched), try to match using spaceId
      if (!taskPath && !task.spaceName) {
        // Task not enriched yet - try to match using spaceId and containersMap
        if (containersMap && containersMap.size > 0 && task.spaceId) {
          // Find all container IDs that match the filter path
          const matchingContainerIds = new Set<string>();
          containersMap.forEach((container, id) => {
            if (matchesFilterPath(container.path, filters.path)) {
              matchingContainerIds.add(id);
            }
          });

          // Check if task's spaceId matches any of the matching containers
          if (matchingContainerIds.size > 0) {
            if (!matchingContainerIds.has(task.spaceId)) {
              return false;
            }
          }
          // If containersMap is available but no matches found, include task (might be a new container)
        }
        // If no containersMap, include task to prevent empty views during loading
      } else {
        // Match if filter path is exactly the task path or is a prefix (for folder/space filtering)
        if (!matchesFilterPath(taskPath, filters.path)) {
          return false;
        }
      }
  }

  // Status filter (case-insensitive)
  if (hasStatusFilter && !statusSet.has(taskStatus)) return false;

  // Priority filter (exclude-missing)
  if (hasPriorityFilter) {
      if (!task.priority) return false;
      if (!prioritySet.has(task.priority)) return false;
  }

  // Assignee filter
  if (hasAssigneeFilter) {
      const assignees = task.assignees || [];
      const hasMatchingAssignee = assignees.some(a => assigneeSet.has(a));
      if (!hasMatchingAssignee) return false;
  }

  // Tag filter
  if (hasTagFilter) {
      const tags = task.tags || [];
      const hasMatchingTag = tags.some(t => tagSet.has(t));
      if (!hasMatchingTag) return false;
  }

  // Date range filter (exclude-missing)
  if (hasDateRangeFilter) {
      if (!task.dueDate) return false;
      const dueTs = typeof task.dueDate === 'number' ? task.dueDate : (task.dueDate && typeof task.dueDate === 'object' && 'getTime' in task.dueDate ? (task.dueDate as Date).getTime() : null);
      if (typeof dueTs !== 'number') return false;
      const startTs = filters.dateRange.start instanceof Date ? filters.dateRange.start.getTime() : (typeof filters.dateRange.start === 'number' ? filters.dateRange.start : undefined);
      const endTs = filters.dateRange.end instanceof Date ? filters.dateRange.end.getTime() : (typeof filters.dateRange.end === 'number' ? filters.dateRange.end : undefined);
      if (typeof startTs === 'number' && dueTs < startTs) return false;
      if (typeof endTs === 'number' && dueTs > endTs) return false;
  }

  // Attachments filter
  const attachments = task.attachments || [];
  if (filters.hasAttachments === true && attachments.length === 0) return false;
  if (filters.hasAttachments === false && attachments.length > 0) return false;

  // Subtasks filter (exclude-missing is naturally satisfied)
  if (filters.hasSubtasks !== null && parentIds) {
      const taskHasSubtasks = parentIds.has(task.id);
      if (filters.hasSubtasks === true && !taskHasSubtasks) return false;
      if (filters.hasSubtasks === false && taskHasSubtasks) return false;
  }

  // Custom fields filter (multi-select for dropdown/boolean)
  if (hasCustomFieldsFilter && filters.customFields) {
      for (const [fieldId, filterValues] of Object.entries(filters.customFields)) {
    if (!filterValues || filterValues.length === 0) continue;

    const taskValue = task.customFields?.[fieldId];

    // If task doesn't have this field, exclude it (exclude-missing rule)
    if (taskValue === null || taskValue === undefined || taskValue === '') {
          return false;
    }

    // Check if task value matches any of the filter values
    const filterValueSet = new Set(filterValues.map(v => String(v)));
    const taskValueStr = String(taskValue);

    // For array values (multi-select), check if any value matches
    if (Array.isArray(taskValue)) {
          const hasMatch = taskValue.some(v => filterValueSet.has(String(v)));
          if (!hasMatch) return false;
    } else {
          // For single values, check direct match
          if (!filterValueSet.has(taskValueStr)) return false;
    }
      }
  }

  // Custom fields text filter (contains search)
  if (filters.customFieldText) {
      for (const [fieldId, searchStr] of Object.entries(filters.customFieldText)) {
    if (!searchStr) continue;
    const taskValue = task.customFields?.[fieldId];
    if (taskValue === null || taskValue === undefined || taskValue === '') return false;
    const taskValueStr = Array.isArray(taskValue) ? taskValue.join(' ') : String(taskValue);
    if (!taskValueStr.toLowerCase().includes(searchStr.toLowerCase())) return false;
      }
  }

  // Custom fields date range filter
  if (filters.customFieldDateRange) {
      for (const [fieldId, range] of Object.entries(filters.customFieldDateRange)) {
    if (!range.start && !range.end) continue;
    const taskValue = task.customFields?.[fieldId];
    if (taskValue === null || taskValue === undefined || taskValue === '') return false;
    const taskDate = new Date(String(taskValue)).getTime();
    if (isNaN(taskDate)) return false;
    if (range.start) {
        const startTs = new Date(range.start).getTime();
        if (taskDate < startTs) return false;
    }
    if (range.end) {
        const endTs = new Date(range.end).getTime();
        if (taskDate > endTs + 86400000 - 1) return false; // end of day
    }
      }
  }

  // Search filter
  if (hasSearch) {
      const matchesName = task.name.toLowerCase().includes(searchLower);
      const matchesContent = task.content.toLowerCase().includes(searchLower);
      const tags = task.tags || [];
      const assignees = task.assignees || [];
      const matchesTag = tags.some(t => t.toLowerCase().includes(searchLower));
      const matchesAssignee = assignees.some(a => a.toLowerCase().includes(searchLower) || (resolveDisplayName && resolveDisplayName(a).toLowerCase().includes(searchLower)));
      const matchesId = task.id.toLowerCase().includes(searchLower);

      // Also search in custom fields
      let matchesCustomField = false;
      if (task.customFields) {
    for (const [_fieldId, value] of Object.entries(task.customFields)) {
          if (value === null || value === undefined) continue;
          const valueStr = Array.isArray(value) ? value.join(' ') : String(value);
          if (valueStr.toLowerCase().includes(searchLower)) {
      matchesCustomField = true;
      break;
          }
    }
      }

      if (!matchesName && !matchesContent && !matchesTag && !matchesAssignee && !matchesId && !matchesCustomField) return false;
  }

  return true;
  });

  return filteredResult;
}

